/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import type { OrderItemTodoResponseArgs } from '@this/src/domain/arrangement/order_item_todo';
import OrderItemTodo, { convertOrderItemTodoResponseToArgs } from '@this/src/domain/arrangement/order_item_todo';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import type { OrderItemJobTypeKey } from '@this/src/domain/order_item/order_item_job_type';
import { OrderItemJobTypeNumKeys } from '@this/src/domain/order_item/order_item_job_type';
import type { OrderItemStatusKey } from '@this/src/domain/order_item/order_item_status';
import { OrderItemStatusOngoing, OrderItemStatusAvailable2 } from '@this/src/domain/order_item/order_item_status';
import type { TripStatusKey } from '@this/src/domain/trip/trip_status';
import { TripStatusOngoing, TripStatusAvailable } from '@this/src/domain/trip/trip_status';
import type { OrderItemCategoryKey } from '@this/src/domain/order_item/order_item_category';
import { OrderItemCategoryKeys } from '@this/src/domain/order_item/order_item_category';
import { Radio } from '@this/src/components/shared/ui/inputs/radio';
import type { ElementQueryKey } from '@this/src/domain/order_item/element_query';
import { ElementQuery, ElementQueryKeys } from '@this/src/domain/order_item/element_query';
import { isAiTravel } from '@this/src/util/utils';
import type { SnoozeStatusKey } from '@this/src/domain/order_item/snooze_status';
import { SnoozeStatus, SnoozeStatusKeys } from '@this/src/domain/order_item/snooze_status';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import type { WaitingStatusKey } from '@this/src/domain/order_item/waiting_status';
import { WaitingStatus, WaitingStatusKeys } from '@this/src/domain/order_item/waiting_status';
import { Select } from '@this/src/components/shared/ui/inputs/select';
import moment from 'moment';
import type { SpQueryKey } from '@this/src/domain/order_item/sp_query';
import { SpQuery, SpQueryKeys } from '@this/src/domain/order_item/sp_query';
import type { UseQrQueryKey } from '@this/src/domain/organization/use_qr_query';
import { UseQrQuery, UseQrQueryKeys } from '@this/src/domain/organization/use_qr_query';
import type { TicketingTypeQueryKey } from '@this/domain/arrangement/ticketing_type_query';
import { TicketingTypeQuery, TICKETING_TYPE_ALL } from '@this/domain/arrangement/ticketing_type_query';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import Notification from '@this/src/notification';
import { Fetcher, HTTPError, getParam, appendScript } from '@this/src/util';
import { Modal } from '@this/shared/ui/feedbacks/modal';
import { Link } from '@this/shared/ui/navigations/link';
import OrderItemStatusSelector from '../order_item_status_selector';
import TripStatusSelector from '../trip_status_selector';
import OrderItemCategorySelector from './order_item_category_selector';
import OrderItemTodoTableRow from './order_item_todo_table_row';
import { SearchArea, SearchBlock, SearchLabel } from '../search_area';
import { TicketCountArea, TicketCountTitle, TicketCountLabel } from '../ticket_count_area';
import useOrganizationSelector from '../use_organization_selector';
import useSuppliedItemSelector from '../use_supplied_item_selector';
import DateRangePicker from '../date_range_picker';
import OrderItemSequenceAbortConfirm from './order_item_sequence_abort_confirm';
import { ExtensionLogs } from '../extension_logs/extension_logs';
import { ExtensionStepLogs } from '../extension_step_logs/extension_step_logs';

type SortKey = 'received_at' | 'started_at' | 'purchase_deadline';
type SortDirection = 'asc' | 'desc';

interface PopoverProps {
  todo: OrderItemTodo | null;
  anchorEl: Element | null;
}

interface Props {
  serviceId: number;
  ticketCount: { [carrier: string]: number };
}

interface PusherChannel {
  name: string;
  bind: <T = unknown>(event: string, callback: (data: T) => void) => void;
  unbind: (event?: string) => void;
}

interface PusherInstance {
  subscribe: (channel: string) => PusherChannel;
  unsubscribe: (channel: string) => void;
  disconnect: () => void;
}

type PusherConstructor = new (key: string, options?: { encrypted?: boolean }) => PusherInstance;

declare global {
  interface Window {
    Pusher: PusherConstructor;
  }
}

interface OrderItemUnlockedItem {
  order_item_id: number;
  trace_id: number;
}

interface OrderItemsUnlockedData {
  items: OrderItemUnlockedItem[];
}

interface OrderItemLockedData {
  order_item_id: number;
  trace_id: number;
  arranger_id: number;
  arranger_name: string;
  work_started_at: string;
}

interface ArrangementMeResponse {
  me: {
    id: number;
    name: string;
  };
  pusherKey: string;
  pusherChannelPrefix: string;
}

interface Response {
  order_item_todos: OrderItemTodoResponseArgs[];
  total_count: number;
  total_page: number;
}

const getDefaultPage = (): number => {
  const str = getParam('page');
  return str ? parseInt(str, 10) : 1;
};

const getDefaultJobTypes = (): OrderItemJobTypeKey[] => {
  return (getParam('job_types')
    ?.split(',')
    .map((str: string) => parseInt(str, 10)) || OrderItemJobTypeNumKeys) as OrderItemJobTypeKey[];
};

const getDefaultStatuses = (): OrderItemStatusKey[] => {
  return (getParam('statuses')
    ?.split(',')
    .map((str: string) => parseInt(str, 10)) || OrderItemStatusOngoing) as OrderItemStatusKey[];
};

const getDefaultTripStatuses = (): TripStatusKey[] => {
  return (utils
    .getParam('trip_statuses')
    ?.split(',')
    .map((str: string) => parseInt(str, 10)) || TripStatusOngoing) as TripStatusKey[];
};

const getDefaultCategories = (): OrderItemCategoryKey[] => {
  return (utils
    .getParam('categories')
    ?.split(',')
    .map((str: string) => (str === 'message' ? str : parseInt(str, 10))) ||
    OrderItemCategoryKeys) as OrderItemCategoryKey[];
};

const getDefaultElementQuery = (): ElementQueryKey => {
  return (getParam('element_query') || 'all') as ElementQueryKey;
};

const getDefaultOnlyOthers = (): boolean => {
  return getParam('only_others') === 'true';
};

const getDefaultOnlyManual = (): boolean => {
  return getParam('only_manual') === 'true';
};

const getDefaultOnlyPremiumSupport = (): boolean => {
  return getParam('only_premium_support') === 'true';
};

const getDefaultOnlySupportAccount = (): boolean => {
  return getParam('only_support_account') === 'true';
};

const getDefaultWaiting = (): WaitingStatusKey => {
  return (getParam('waiting') || 'without') as WaitingStatusKey;
};

const getDefaultSp = (): WaitingStatusKey => {
  return (getParam('sp') || 'all') as SpQueryKey;
};

const getDefaultUseQr = (): UseQrQueryKey => {
  return (getParam('use_qr') || 'all') as UseQrQueryKey;
};

const getDefaultTicketingType = (): TicketingTypeQueryKey => {
  return (getParam('ticketing_type') || 9) as TicketingTypeQueryKey;
};

const getDefaultTripId = (): number | undefined => {
  const str = getParam('trip_id');
  if (!str) {
    return undefined;
  }
  return parseInt(str, 10);
};

const getDefaultStartDateFrom = (): Moment | undefined => {
  const str = getParam('start_date_from');
  return str ? moment(str) : undefined;
};

const getDefaultStartDateTo = (): Moment | undefined => {
  const str = getParam('start_date_to');
  return str ? moment(str) : undefined;
};

const getDefaultReceiveDateFrom = (): Moment | undefined => {
  const str = getParam('receive_date_from');
  return str ? moment(str) : undefined;
};

const getDefaultReceiveDateTo = (): Moment | undefined => {
  const str = getParam('receive_date_to');
  return str ? moment(str) : undefined;
};

const getDefaultOnlyUnchanged = (): boolean => {
  return getParam('only_unchanged') === 'true';
};

const getDefaultOnlyRequestForm = (): boolean => {
  return getParam('only_request_form') === 'true';
};

const getDefaultSortKey = (): SortKey => {
  return (getParam('sort_key') || 'started_at') as SortKey;
};

const getDefaultSortDirection = (): SortDirection => {
  return (getParam('sort_direction') || 'asc') as SortDirection;
};

const getDefaultSnoozeStatus = (): SnoozeStatusKey => {
  return (getParam('snooze_status') || 'todo') as SnoozeStatusKey;
};

interface StartDefaultSequenceRequest {
  todo: OrderItemTodo;
  fetch: () => void;
}

const fetchStartDefaultSequence = async ({ todo, fetch }: StartDefaultSequenceRequest) => {
  Fetcher.post('/arrangement/order_item_todos/start_default_sequence.json', {
    order_item_id: todo.orderItemId
  })
    .then(() => {
      fetch();
    })
    .catch(error => {
      const message =
        error instanceof HTTPError && error.response?.data.message
          ? error.response.data.message
          : '標準シーケンスの開始に失敗しました';
      Notification.error(message);
    });
};

interface AbortRequest {
  todo: OrderItemTodo;
  fetch: () => void;
}

const fetchAbortMasterStepSequence = async ({ todo, fetch }: AbortRequest) => {
  Fetcher.post('/arrangement/order_item_todos/abort_master_sequence.json', {
    order_item_id: todo.orderItemId
  })
    .then(() => {
      fetch();
    })
    .catch(error => {
      const message =
        error instanceof HTTPError && error.response?.data.message
          ? error.response.data.message
          : '更新に失敗しました';
      Notification.error(message);
    });
};

const OrderItemTodoList = observer(({ serviceId, ticketCount }: Props) => {
  const { current: history } = useRef(useHistory());
  const [isLoading, setIsLoading] = useState(false);
  const [todos, setTodos] = useState<OrderItemTodo[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(getDefaultPage());
  const [currentArrangerName, setCurrentArrangerName] = useState<string>('');
  const [currentArrangerId, setCurrentArrangerId] = useState<number | null>(null); // ID追加
  const [pusherKey, setPusherKey] = useState<string | null>(null);
  const [pusherChannelPrefix, setPusherChannelPrefix] = useState<string | null>(null);
  const pusherRef = useRef<PusherInstance | null>(null);
  const channelRef = useRef<PusherChannel | null>(null);
  const [jobTypes, setJobTypes] = useState<OrderItemJobTypeKey[]>(getDefaultJobTypes());
  const [statuses, setStatuses] = useState<OrderItemStatusKey[]>(getDefaultStatuses());
  const [tripStatuses, setTripStatuses] = useState<TripStatusKey[]>(getDefaultTripStatuses());
  const [categories, setCategories] = useState<OrderItemCategoryKey[]>(getDefaultCategories());
  const [elementQuery, setElementQuery] = useState<ElementQueryKey>(getDefaultElementQuery());
  const [snoozeStatus, setSnoozeStatus] = useState<SnoozeStatusKey>(getDefaultSnoozeStatus());
  const [onlyOthers, setOnlyOthers] = useState<boolean>(getDefaultOnlyOthers());
  const [onlyManual, setOnlyManual] = useState<boolean>(getDefaultOnlyManual());
  const [onlyPremiumSupport, setOnlyPremiumSupport] = useState<boolean>(getDefaultOnlyPremiumSupport());
  const [onlySupportAccount, setOnlySupportAccount] = useState<boolean>(getDefaultOnlySupportAccount());
  const [onlyReceiptRevisionInProgress, setOnlyReceiptRevisionInProgress] = useState<boolean>(false);
  const [waiting, setWaiting] = useState<WaitingStatusKey>(getDefaultWaiting());
  const [sp, setSp] = useState<SpQueryKey>(getDefaultSp());
  const [useQr, setUseQr] = useState<UseQrQueryKey>(getDefaultUseQr());
  const [ticketingType, setTicketingType] = useState<TicketingTypeQueryKey>(getDefaultTicketingType());
  const [tripId, setTripId] = useState<number | undefined>(getDefaultTripId());
  const [startDateFrom, setStartDateFrom] = useState<Moment | undefined>(getDefaultStartDateFrom());
  const [startDateTo, setStartDateTo] = useState<Moment | undefined>(getDefaultStartDateTo());
  const [receiveDateFrom, setReceiveDateFrom] = useState<Moment | undefined>(getDefaultReceiveDateFrom());
  const [receiveDateTo, setReceiveDateTo] = useState<Moment | undefined>(getDefaultReceiveDateTo());
  const [onlyUnchanged, setOnlyUnchanged] = useState<boolean>(getDefaultOnlyUnchanged());
  const [onlyRequestForm, setOnlyRequestForm] = useState<boolean>(getDefaultOnlyRequestForm());
  const [organizationIds, renderOrganizationSelector, setOrganizationIds] = useOrganizationSelector();
  const [suppliedItemIds, renderSuppliedItemSelector, setSuppliedItemIds] = useSuppliedItemSelector();

  const [sortKey, setSortKey] = useState<SortKey>(getDefaultSortKey());
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection());
  const [sequenceAbortConfirm, setSequenceAbortConfirm] = useState<PopoverProps>({ todo: null, anchorEl: null });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTraceId, setSelectedTraceId] = useState<string>('');
  const [extensionLogTab, setExtensionLogTab] = useState<'page' | 'step'>('page');

  const fetchTodos = useCallback(() => {
    setIsLoading(true);

    const params = {
      page,
      job_types: jobTypes,
      statuses,
      trip_statuses: tripStatuses,
      categories,
      element_query: elementQuery,
      snooze_status: snoozeStatus,
      only_others: onlyOthers,
      only_manual: onlyManual,
      only_premium_support: onlyPremiumSupport,
      only_support_account: onlySupportAccount,
      waiting,
      sp,
      use_qr: useQr,
      ticketing_type: ticketingType,
      trip_id: tripId,
      start_date_from: startDateFrom?.format('YYYY-MM-DD'),
      start_date_to: startDateTo?.format('YYYY-MM-DD'),
      receive_date_from: receiveDateFrom?.format('YYYY-MM-DD'),
      receive_date_to: receiveDateTo?.format('YYYY-MM-DD'),
      only_unchanged: onlyUnchanged,
      only_request_form: onlyRequestForm,
      only_receipt_revision_in_progress: onlyReceiptRevisionInProgress,
      organization_ids: organizationIds.join(','),
      supplied_item_ids: suppliedItemIds.length > 0 ? suppliedItemIds.join(',') : undefined,
      sort_key: sortKey,
      sort_direction: sortDirection
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });
    const query = _.map(params, (value, key) => `${key}=${value}`).join('&');
    history.push(`/arrangement/todo_list?tab=ORDER_ITEM&${query}`);

    Fetcher.get<Response>('/arrangement/order_item_todos.json', params)
      .then(res => {
        setTodos(res.order_item_todos.map(args => new OrderItemTodo(convertOrderItemTodoResponseToArgs(args))));
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }
      })
      .finally(() => setIsLoading(false));
  }, [
    page,
    jobTypes,
    statuses,
    tripStatuses,
    categories,
    elementQuery,
    snoozeStatus,
    onlyOthers,
    onlyManual,
    onlyPremiumSupport,
    onlySupportAccount,
    waiting,
    sp,
    useQr,
    ticketingType,
    tripId,
    startDateFrom,
    startDateTo,
    receiveDateFrom,
    receiveDateTo,
    onlyUnchanged,
    onlyRequestForm,
    onlyReceiptRevisionInProgress,
    organizationIds,
    suppliedItemIds,
    sortKey,
    sortDirection,
    history
  ]);

  useEffect(() => {
    fetchTodos();

    appendScript('https://js.pusher.com/3.0/pusher.min.js');
    Fetcher.get<ArrangementMeResponse>('/arrangement/me.json')
      .then(response => {
        setCurrentArrangerName(response.me?.name || '');
        setCurrentArrangerId(response.me?.id || null);
        setPusherKey(response.pusherKey || '');
        setPusherChannelPrefix(response.pusherChannelPrefix || '');
      })
      .catch(error => {
        console.error('[Pusher初期化] エラー:', error);
      });
  }, [fetchTodos]);

  useEffect(() => {
    if (!pusherKey || pusherChannelPrefix === null || pusherChannelPrefix === undefined) return (): void => {};

    let timeoutId: number | undefined;
    const initPusher = () => {
      if (typeof window.Pusher !== 'undefined') {
        pusherRef.current = new window.Pusher(pusherKey, { encrypted: true });
        channelRef.current = pusherRef.current.subscribe(`${pusherChannelPrefix}todo_working_status`);

        channelRef.current.bind('order_item_locked', (data: OrderItemLockedData) => {
          setTodos(prevTodos => {
            const newTodos = [...prevTodos];
            const todo = newTodos.find(t => t.orderItemId === data.order_item_id || t.traceId === data.trace_id);
            if (todo) {
              todo.isWorking = true;
              todo.workingArrangerName = data.arranger_name;
              todo.workStartedAt = data.work_started_at ? moment(data.work_started_at) : null;
              todo.isLockedByOthers = data.arranger_id !== currentArrangerId;
            }
            return newTodos;
          });
        });

        // アンロックイベント
        channelRef.current.bind('order_items_unlocked', (data: OrderItemsUnlockedData) => {
          setTodos(prevTodos => {
            const newTodos = [...prevTodos];
            data.items?.forEach((item: OrderItemUnlockedItem) => {
              const todo = newTodos.find(t => t.orderItemId === item.order_item_id || t.traceId === item.trace_id);
              if (todo) {
                todo.isWorking = false;
                todo.workingArrangerName = null;
                todo.workStartedAt = null;
                todo.workEndedAt = null;
                todo.isLockedByOthers = false;
              }
            });

            return newTodos;
          });
        });
      } else {
        timeoutId = window.setTimeout(initPusher, 100);
      }
    };
    initPusher();

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (channelRef.current && pusherRef.current) {
        pusherRef.current.unsubscribe(channelRef.current.name);
        pusherRef.current.disconnect();
      }
    };
  }, [pusherKey, pusherChannelPrefix, currentArrangerName, currentArrangerId]);

  const markMessageFinish = (messageId: number) => {
    setIsLoading(true);
    Fetcher.put(`/arrangement/messages/${messageId}/finish`, {}).then(() => fetchTodos());
  };

  const saveSnooze = useCallback(
    (todo: OrderItemTodo) => {
      setIsLoading(true);
      const params = {
        order_item_id: todo.orderItemId,
        snooze_to: todo.snoozeTo?.format('YYYY-MM-DD HH:mm'),
        memo: todo.snoozeMessage
      };
      Fetcher.post('/arrangement/order_item_snoozed_todos', params).then(() => fetchTodos());
    },
    [fetchTodos]
  );

  const cancelSnooze = useCallback(
    (todo: OrderItemTodo) => {
      setIsLoading(true);
      const params = {
        order_item_id: todo.orderItemId
      };
      Fetcher.put('/arrangement/order_item_snoozed_todos/cancel', params).then(() => fetchTodos());
    },
    [fetchTodos]
  );

  const handleSequenceAdd = useCallback(
    (todo: OrderItemTodo) => () => {
      fetchStartDefaultSequence({ todo, fetch: fetchTodos });
    },
    [fetchTodos]
  );

  const handleSequenceAbortConfirmOpen = useCallback(
    (todo: OrderItemTodo) => () => {
      setSequenceAbortConfirm({ todo, anchorEl: null });
    },
    []
  );

  const handleSequenceAbortConfirmClose = useCallback(
    () => setSequenceAbortConfirm({ todo: null, anchorEl: null }),
    []
  );

  const handleSequenceAbortConfirmAbort = useCallback(
    (todo: OrderItemTodo) => {
      fetchAbortMasterStepSequence({ todo, fetch: fetchTodos });
    },
    [fetchTodos]
  );

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedTraceId('');
    setExtensionLogTab('page');
  };

  const handleSameDayOrderTeamShortcut = useCallback(() => {
    const today = moment();
    const allCategoriesExceptMessage = OrderItemCategoryKeys.filter(
      key => key !== 'message'
    ) as OrderItemCategoryKey[];

    setJobTypes([0]);
    setStatuses([...OrderItemStatusOngoing]);
    setTripStatuses([...TripStatusOngoing]);
    setCategories(allCategoriesExceptMessage);
    setOrganizationIds?.([]);
    setSuppliedItemIds?.([]);
    setElementQuery('all');
    setSnoozeStatus('todo');
    setWaiting('without');
    setSp('all');
    setUseQr('all');
    setTripId(undefined);
    setStartDateFrom(undefined);
    setStartDateTo(undefined);
    setReceiveDateFrom(today.clone().startOf('day'));
    setReceiveDateTo(today.clone().endOf('day'));
    setTicketingType(9 as TicketingTypeQueryKey);
    setSortKey('started_at');
    setSortDirection('asc');
    setOnlyOthers(false);
    setOnlyUnchanged(false);
    setOnlyManual(false);
    setOnlyPremiumSupport(false);
    setOnlyRequestForm(false);
    setOnlySupportAccount(false);
    setOnlyReceiptRevisionInProgress(false);
    setPage(1);
  }, []);

  const handleOverseas24hShortcut = useCallback(() => {
    Fetcher.get<{ supplied_items: Array<{ id: number; name: string; supplier: { name: string } }> }>(
      '/arrangement/supplied_items.json'
    ).then(res => {
      const matchedIds = res.supplied_items
        .filter(item => item.supplier?.name?.includes('アーク・スリー・インターナショナル'))
        .map(item => item.id);
      ReactDOM.unstable_batchedUpdates(() => {
        setJobTypes(OrderItemJobTypeNumKeys as OrderItemJobTypeKey[]);
        setStatuses([9, 3, 8, 10, 11, 12, 13] as OrderItemStatusKey[]);
        setTripStatuses([0, 3, 4, 6] as TripStatusKey[]);
        setCategories([2] as OrderItemCategoryKey[]);
        setOrganizationIds?.([]);
        setSuppliedItemIds?.(matchedIds);
        setElementQuery('all');
        setSnoozeStatus('todo');
        setWaiting('without');
        setSp('all');
        setUseQr('all');
        setTripId(undefined);
        setStartDateFrom(undefined);
        setStartDateTo(undefined);
        setReceiveDateFrom(undefined);
        setReceiveDateTo(undefined);
        setTicketingType(9 as TicketingTypeQueryKey);
        setSortKey('started_at');
        setSortDirection('asc');
        setOnlyOthers(false);
        setOnlyUnchanged(false);
        setOnlyManual(false);
        setOnlyPremiumSupport(false);
        setOnlyRequestForm(false);
        setOnlySupportAccount(false);
        setOnlyReceiptRevisionInProgress(false);
        setPage(1);
      });
    });
  }, []);

  const handleReceiptRevisionShortcut = useCallback(() => {
    const allCategoriesExceptMessage = OrderItemCategoryKeys.filter(
      key => key !== 'message'
    ) as OrderItemCategoryKey[];

    setJobTypes(OrderItemJobTypeNumKeys);
    setStatuses([...OrderItemStatusAvailable2]);
    setTripStatuses([...TripStatusAvailable]);
    setCategories(allCategoriesExceptMessage);
    setOrganizationIds?.([]);
    setSuppliedItemIds?.([]);
    setElementQuery('all');
    setSnoozeStatus('all');
    setWaiting('all');
    setSp('all');
    setUseQr('all');
    setTripId(undefined);
    setStartDateFrom(undefined);
    setStartDateTo(undefined);
    setReceiveDateFrom(undefined);
    setReceiveDateTo(undefined);
    setTicketingType(9 as TicketingTypeQueryKey);
    setSortKey('started_at');
    setSortDirection('asc');
    setOnlyOthers(false);
    setOnlyUnchanged(false);
    setOnlyManual(false);
    setOnlyPremiumSupport(false);
    setOnlyRequestForm(false);
    setOnlySupportAccount(false);
    setOnlyReceiptRevisionInProgress(true);
    setPage(1);
  }, []);

  const handleHikitsugiShortcut = useCallback(() => {
    const allCategoriesExceptMessage = OrderItemCategoryKeys.filter(
      key => key !== 'message'
    ) as OrderItemCategoryKey[];

    setJobTypes(OrderItemJobTypeNumKeys);
    setStatuses([...OrderItemStatusAvailable2]);
    setTripStatuses([...TripStatusAvailable]);
    setCategories(allCategoriesExceptMessage);
    setOrganizationIds?.([]);
    setElementQuery('all');
    setSnoozeStatus('todo');
    setWaiting('only');
    setSp('all');
    setUseQr('all');
    setTripId(undefined);
    setStartDateFrom(undefined);
    setStartDateTo(undefined);
    setReceiveDateFrom(undefined);
    setReceiveDateTo(undefined);
    setTicketingType(TICKETING_TYPE_ALL as TicketingTypeQueryKey);
    setSortKey('started_at');
    setSortDirection('asc');
    setOnlyOthers(false);
    setOnlyUnchanged(false);
    setOnlyManual(false);
    setOnlyPremiumSupport(false);
    setOnlyRequestForm(false);
    setOnlySupportAccount(false);
    setOnlyReceiptRevisionInProgress(false);
    setPage(1);
  }, []);

  return (
    <>
      {(ticketCount.ANA > 0 || ticketCount.JAL > 0 || ticketCount.SFJ > 0) && (
        <TicketCountArea>
          <TicketCountTitle>未使用株優枚数</TicketCountTitle>
          {Object.keys(ticketCount).map(t => (
            <TicketCountLabel key={t}>
              {t}: {ticketCount[t]}
            </TicketCountLabel>
          ))}
        </TicketCountArea>
      )}
      <SearchArea>
        <SearchBlock>
          <SearchLabel>商品ステータス</SearchLabel>
          <OrderItemStatusSelector
            selectedOrderItemJobTypes={jobTypes}
            onChangeSelectedOrderItemJobTypes={setJobTypes}
            selectedOrderItemStatuses={statuses}
            onChangeSelectedOrderItemStatuses={setStatuses}
          />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>旅程ステータス</SearchLabel>
          <TripStatusSelector selectedTripStatuses={tripStatuses} onChangeSelectedTripStatuses={setTripStatuses} />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>カテゴリ</SearchLabel>
          <OrderItemCategorySelector selectedOrderItemCategories={categories} onChange={setCategories} />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>仕入商品</SearchLabel>
          {renderSuppliedItemSelector()}
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>エレメント</SearchLabel>
          <Select
            value={elementQuery}
            onChange={e => setElementQuery(e.target.value as ElementQueryKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {ElementQueryKeys.map(key => (
              <option key={key} value={key}>
                {ElementQuery[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>スヌーズ</SearchLabel>
          <Select
            value={snoozeStatus}
            onChange={e => setSnoozeStatus(e.target.value as SnoozeStatusKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {SnoozeStatusKeys.map(key => (
              <option key={key} value={key}>
                {SnoozeStatus[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>引き継ぎ</SearchLabel>
          <Select
            value={waiting}
            onChange={e => setWaiting(e.target.value as WaitingStatusKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {WaitingStatusKeys.map(key => (
              <option key={key} value={key}>
                {WaitingStatus[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>SP</SearchLabel>
          <Select
            value={sp}
            onChange={e => setSp(e.target.value as SpQueryKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {SpQueryKeys.map(key => (
              <option key={key} value={key}>
                {SpQuery[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>QR利用企業</SearchLabel>
          <Select
            value={useQr}
            onChange={e => setUseQr(e.target.value as UseQrQueryKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {UseQrQueryKeys.map(key => (
              <option key={key} value={key}>
                {UseQrQuery[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>Trip ID</SearchLabel>
          <Input
            value={tripId || ''}
            onChange={e => {
              if (_.isEmpty(e.target.value)) {
                setTripId(undefined);
              } else {
                setTripId(parseInt(e.target.value, 10));
              }
            }}
            style={{ width: '80px' }}
          />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>出発日</SearchLabel>
          <DateRangePicker
            from={startDateFrom}
            to={startDateTo}
            onFromChange={d => setStartDateFrom(d)}
            onToChange={d => setStartDateTo(d)}
          />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>依頼日</SearchLabel>
          <DateRangePicker
            from={receiveDateFrom}
            to={receiveDateTo}
            onFromChange={d => setReceiveDateFrom(d)}
            onToChange={d => setReceiveDateTo(d)}
          />
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>発券方法</SearchLabel>
          <Select
            value={ticketingType}
            onChange={e => setTicketingType(e.target.value as TicketingTypeQueryKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {TicketingTypeQuery(serviceId).map(d => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>法人</SearchLabel>
          {renderOrganizationSelector()}
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyOthers} onChange={e => setOnlyOthers(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>ステータス最終更新者が自分以外</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyUnchanged} onChange={e => setOnlyUnchanged(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>依頼時からの内容変更がない商品のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyManual} onChange={e => setOnlyManual(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>手動作成のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyPremiumSupport} onChange={e => setOnlyPremiumSupport(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>土日祝オプション企業のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyRequestForm} onChange={e => setOnlyRequestForm(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>リクエストフォーム経由のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlySupportAccount} onChange={e => setOnlySupportAccount(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>SA予約のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <Checkbox
            checked={onlyReceiptRevisionInProgress}
            onChange={e => setOnlyReceiptRevisionInProgress(e.target.checked)}
          >
            <b style={{ fontSize: '12px' }}>破棄訂正中のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>並び順</SearchLabel>
          <Select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            <option value="received_at">依頼日</option>
            <option value="started_at">出発日</option>
            <option value="purchase_deadline">購入期限日</option>
          </Select>
          <Radio
            checked={sortDirection === 'asc'}
            onChange={() => setSortDirection('asc')}
            style={{ marginRight: '5px' }}
          >
            昇順▲
          </Radio>
          <Radio checked={sortDirection === 'desc'} onChange={() => setSortDirection('desc')}>
            降順▼
          </Radio>
        </SearchBlock>
        <div style={{ width: '100%', borderTop: '1px solid #ddd', margin: '10px' }}>
          <h3 style={{ fontSize: '1em', fontWeight: 'bold', margin: '10px 0' }}>検索ショートカット</h3>
          <Link style={{ cursor: 'pointer', marginRight: '15px' }} onClick={handleSameDayOrderTeamShortcut}>
            当日オーダーチーム
          </Link>
          <Link style={{ cursor: 'pointer', marginRight: '15px' }} onClick={handleReceiptRevisionShortcut}>
            要領収書変更処理
          </Link>
          <Link style={{ cursor: 'pointer', marginRight: '15px' }} onClick={handleHikitsugiShortcut}>
            引き継ぎ案件
          </Link>
          {isAiTravel(serviceId) && (
            <Link style={{ cursor: 'pointer' }} onClick={handleOverseas24hShortcut}>
              海外24時間365日対象
            </Link>
          )}
        </div>
      </SearchArea>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div>検索結果: {totalCount}件</div>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Tripステータス</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>ステータス最終更新者</TableCell>
                <TableCell>カテゴリ</TableCell>
                <TableCell>シーケンス</TableCell>
                <TableCell>変更</TableCell>
                <TableCell>法人名</TableCell>
                <TableCell>SP</TableCell>
                <TableCell>QR</TableCell>
                <TableCell>土日祝対応</TableCell>
                <TableCell>引き継ぎ</TableCell>
                <TableCell>発券方法</TableCell>
                <TableCell nowrap>出発日</TableCell>
                <TableCell nowrap>依頼日</TableCell>
                <TableCell nowrap>購入期限日</TableCell>
                <TableCell>チャットメッセージ</TableCell>
                <TableCell nowrap>Trip ID</TableCell>
                <TableCell nowrap>Trace ID</TableCell>
                <TableCell>最新のログメッセージ</TableCell>
                {/* → 棚卸し画面に実装すべき<TableCell>AI Operator ログ</TableCell> */}
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {todos.map((todo, i) => (
                <OrderItemTodoTableRow
                  key={i}
                  todo={todo}
                  onMessageFinish={markMessageFinish}
                  onSaveSnooze={saveSnooze}
                  onCancelSnooze={cancelSnooze}
                  onSequenceAdd={handleSequenceAdd}
                  onSequenceAbortOpen={handleSequenceAbortConfirmOpen}
                  fetchOrderItemTodos={fetchTodos}
                  serviceId={serviceId}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            count={totalPage}
            page={page}
            onChange={(_e: unknown, newPage: number) => setPage(newPage)}
          />
        </>
      )}
      <OrderItemSequenceAbortConfirm
        todo={sequenceAbortConfirm.todo}
        onAbort={handleSequenceAbortConfirmAbort}
        onClose={handleSequenceAbortConfirmClose}
      />
      <Modal title="AI Operator 動作ログ" open={isModalVisible} onClose={handleModalClose} size="large">
        {selectedTraceId && (
          <>
            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '16px' }}>
              <button
                type="button"
                onClick={() => setExtensionLogTab('page')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderBottom: extensionLogTab === 'page' ? '2px solid #1976d2' : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: extensionLogTab === 'page' ? 'bold' : 'normal',
                  color: extensionLogTab === 'page' ? '#1976d2' : '#666'
                }}
              >
                ページログ
              </button>
              <button
                type="button"
                onClick={() => setExtensionLogTab('step')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderBottom: extensionLogTab === 'step' ? '2px solid #1976d2' : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: extensionLogTab === 'step' ? 'bold' : 'normal',
                  color: extensionLogTab === 'step' ? '#1976d2' : '#666'
                }}
              >
                ステップログ
              </button>
            </div>
            {extensionLogTab === 'page' ? (
              <ExtensionLogs traceId={selectedTraceId} />
            ) : (
              <ExtensionStepLogs traceId={selectedTraceId} />
            )}
          </>
        )}
      </Modal>
    </>
  );
});

export default OrderItemTodoList;
